import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AccountResponse } from "api/auth/data-contracts";
import { createApi } from "util/ApiCreator";
import { AccountController } from "api/auth/AccountController";
import { useAppSelector } from "redux/hooks";
import { hasRole } from "util/UtilityFunctions";
import { RoleNames } from "models/auth/RoleNames";
import { adminTheme } from "theme/adminTheme";
import {
  Box,
  Container,
  Typography,
  Card,
  Button,
  LinearProgress,
  Chip,
  Grid,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

export default function AccountPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useRef(createApi("account") as AccountController);
  const me = useAppSelector((s) => s?.me?.info);
  const [account, setAccount] = useState<AccountResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const isMyAccount = !id || id === "me";
  const isAdmin = hasRole(RoleNames.ADMIN, me?.roles);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function getData() {
    setLoading(true);
    try {
      if (isMyAccount) {
        const data = await api.current.getMe();
        setAccount(data);
      } else if (id && isAdmin) {
        // For admin viewing other accounts, we'd need findById
        // For now, just use the accounts list to find the account
        const allAccounts = await api.current.findAll();
        const found = allAccounts.find((a: AccountResponse) => a.id === Number(id));
        setAccount(found || null);
      }
    } catch (error) {
      console.error("Error fetching account:", error);
    }
    setLoading(false);
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("lt-LT");
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "ROLE_ADMIN":
        return "Administratorius";
      case "ROLE_USER":
        return "Vartotojas";
      default:
        return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "ROLE_ADMIN":
        return { bg: "rgba(239, 68, 68, 0.1)", text: "#EF4444" };
      case "ROLE_USER":
        return { bg: "rgba(99, 102, 241, 0.1)", text: adminTheme.colors.primary };
      default:
        return { bg: adminTheme.colors.backgroundAlt, text: adminTheme.colors.textSecondary };
    }
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!account) {
    return (
      <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Card
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <PersonIcon sx={{ fontSize: 48, color: adminTheme.colors.textMuted, mb: 2 }} />
            <Typography variant="h6" sx={{ color: adminTheme.colors.textPrimary, mb: 1 }}>
              Paskyra nerasta
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mt: 2 }}
            >
              Grįžti
            </Button>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderColor: adminTheme.colors.border,
                color: adminTheme.colors.textSecondary,
                "&:hover": {
                  borderColor: adminTheme.colors.primary,
                  bgcolor: `${adminTheme.colors.primary}08`,
                  color: adminTheme.colors.primary,
                },
              }}
            >
              Atgal
            </Button>
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: adminTheme.colors.textPrimary,
            }}
          >
            {isMyAccount ? "Mano paskyra" : "Paskyros informacija"}
          </Typography>
          <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
            {isMyAccount ? "Peržiūrėkite savo paskyros duomenis" : "Paskyros detalės"}
          </Typography>
        </Box>

        {/* Account Card */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          {/* Card Header */}
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${adminTheme.colors.primary}08 0%, ${adminTheme.colors.background} 100%)`,
              borderBottom: `1px solid ${adminTheme.colors.border}`,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: adminTheme.borderRadius.md,
                background: adminTheme.gradients.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <PersonIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}>
                {account.email}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                {account.roles?.map((role) => {
                  const colors = getRoleColor(role);
                  return (
                    <Chip
                      key={role}
                      label={getRoleLabel(role)}
                      size="small"
                      sx={{
                        bgcolor: colors.bg,
                        color: colors.text,
                        fontWeight: 600,
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Card Content */}
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Email */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: adminTheme.borderRadius.sm,
                      bgcolor: adminTheme.colors.backgroundAlt,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: adminTheme.colors.textMuted,
                    }}
                  >
                    <EmailIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      El. paštas
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                      {account.email || "-"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Phone */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: adminTheme.borderRadius.sm,
                      bgcolor: adminTheme.colors.backgroundAlt,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: adminTheme.colors.textMuted,
                    }}
                  >
                    <PhoneIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Telefonas
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                      {account.phone || "-"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Person Code */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: adminTheme.borderRadius.sm,
                      bgcolor: adminTheme.colors.backgroundAlt,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: adminTheme.colors.textMuted,
                    }}
                  >
                    <BadgeIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Asmens kodas
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                      {account.personCode || "-"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Roles */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: adminTheme.borderRadius.sm,
                      bgcolor: adminTheme.colors.backgroundAlt,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: adminTheme.colors.textMuted,
                    }}
                  >
                    <AdminPanelSettingsIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Rolės
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                      {account.roles?.map((role) => {
                        const colors = getRoleColor(role);
                        return (
                          <Chip
                            key={role}
                            label={getRoleLabel(role)}
                            size="small"
                            sx={{
                              bgcolor: colors.bg,
                              color: colors.text,
                              fontWeight: 600,
                              height: 24,
                            }}
                          />
                        );
                      }) || "-"}
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Created */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: adminTheme.borderRadius.sm,
                      bgcolor: adminTheme.colors.backgroundAlt,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: adminTheme.colors.textMuted,
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Sukurta
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                      {formatDate(account.created)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Last Login */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: adminTheme.borderRadius.sm,
                      bgcolor: adminTheme.colors.backgroundAlt,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: adminTheme.colors.textMuted,
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Paskutinis prisijungimas
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                      {formatDate(account.lastLogin)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Account Status */}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                    Paskyros būsena:
                  </Typography>
                  <Chip
                    label={account.fullyCreated ? "Pilnai sukurta" : "Nepilna registracija"}
                    size="small"
                    sx={{
                      bgcolor: account.fullyCreated
                        ? "rgba(16, 185, 129, 0.1)"
                        : "rgba(245, 158, 11, 0.1)",
                      color: account.fullyCreated
                        ? adminTheme.colors.success
                        : adminTheme.colors.warning,
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Container>
    </Box>
  );
}
